import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { useAttendeeMessaging } from '@context/AttendeeMessagingContext';
import { LocalizationContext, useTranslate } from 'nucleus-text';
import { LoadingSpinner } from '@cvent/carina/components/LoadingSpinner';
import ErrorState from '@components/shared/ErrorState';
import { injectTestId } from '@cvent/nucleus-test-automation';
import {
  Chat,
  ChannelList as StreamChannelList,
  LoadMorePaginator,
  ChannelPreviewUIComponentProps
} from 'stream-chat-react';
import { chatLocalization } from '@components/chat/shared/chatLocalization';
import Alert from '@illustrations/Alert';
import { useRouter } from 'next/router';
import Link from 'next/link';
import useTheme from '@cvent/blocks/hooks/useTheme';
import Button from '@cvent/carina/components/Button';
import { TextLink } from '@cvent/carina/components/TextLink';
import { useAttendeeHubConfig } from '@hooks/useAttendeeHubConfig';
import { CSSObject } from '@emotion/react';
import { APIErrorResponse, ErrorFromResponse } from 'stream-chat';
import { StreamType } from '@components/chat/shared/useStreamChat';
import useChannelMembers from '../useChannelMembers';
import MessagingChannelPreview from '../MessagingChannelPreview';
import MessagingChannelList from '../MessagingChannelList';
import EmptyChannelList from '../EmptyChannelList';
import getChannelListCriteria from '../getChannelListCriteria';

const LIMIT = 6;

const useStyle = ({ hasLoadedChannels, isLoadingChannels, flyoutStyle }): Record<string, CSSObject> => {
  const theme = useTheme();
  const buttonPadding = flyoutStyle ? '24px 0 0 0' : '16px 0 0 0';
  return {
    container: {
      '.str-chat': {
        height: 'auto',
        a: {
          paddingTop: flyoutStyle ? 16 : null,
          marginTop: flyoutStyle ? 0 : null,
          marginBottom: flyoutStyle ? 0 : null
        },
        'a:hover': { backgroundColor: flyoutStyle ? theme.block.accent : null }
      },
      '.str-chat-channel-list': {
        float: 'none'
      },
      '.str-chat.messaging': {
        backgroundColor: 'transparent'
      },
      '@media screen and (max-width: 960px)': {
        '.str-chat-channel-list.messaging': {
          position: 'static',
          minHeight: 'auto',
          boxShadow: 'none'
        }
      },
      '.str-chat__channel-list': {
        boxShadow: 'none'
      }
    },
    actions: {
      display: 'flex',
      flex: '1 1 auto',
      justifyContent: 'center',
      padding: hasLoadedChannels ? buttonPadding : '24px 0',
      '> a': {
        textDecoration: 'underline'
      }
    },
    newMessage: {
      '> a': {
        textDecoration: 'none'
      }
    },
    loadingOverlay: {
      display: isLoadingChannels ? 'flex' : 'none',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: theme.block.base,
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      zIndex: 1
    }
  };
};

function ChannelList({
  setHasLoadedChannels,
  onNavFlyoutClose,
  flyoutStyle
}: {
  setHasLoadedChannels?: (hasLoadedChannels: boolean) => void;
  onNavFlyoutClose?: () => void;
  flyoutStyle?: boolean;
}): JSX.Element {
  const { client } = useAttendeeMessaging();
  const router = useRouter();
  const { eventId } = router.query;
  const { translate } = useTranslate();
  const {
    localeObject,
    textResolver: { locale }
  } = useContext(LocalizationContext);

  const [i18nInstance, setI18nInstance] = useState(null);

  useEffect(() => {
    if (locale && localeObject?.default) {
      const setupLocalization = async () => {
        setI18nInstance(await chatLocalization(locale, localeObject));
      };
      setupLocalization();
    }
  }, [locale, localeObject]);

  const [loadedChannels, setLoadedChannels] = useState([]);
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);
  const [loadingChannelsError, setLoadingChannelsError] = useState<
    ErrorFromResponse<APIErrorResponse> | null | boolean
  >(false);

  useChannelMembers({ loadedChannels, limit: LIMIT });

  const hasLoadedChannels = loadedChannels.length !== 0;
  const styles = useStyle({ hasLoadedChannels, isLoadingChannels, flyoutStyle });

  const updateLoadedChannels = useCallback(
    channels => {
      setLoadedChannels(channels);
      if (channels.length > 0) {
        setHasLoadedChannels(true);
      }
    },
    [setHasLoadedChannels, setLoadedChannels]
  );

  // Passing in an empty function to override Stream's defaults,
  // so that the new channel doesn't appear in the channel list
  // until a message is sent.
  const onAddedToChannel = useCallback(() => null, []);
  const { isFeatureSettingEnabled } = useAttendeeHubConfig();
  const isAttendeeListEnabled = isFeatureSettingEnabled('ATTENDEE_LIST');

  const filterCriteria = useMemo(
    () => getChannelListCriteria({ userId: client.userID, limit: LIMIT }),
    [client.userID]
  );

  if (loadingChannelsError) {
    return (
      <ErrorState
        title={translate('unexpected_error_title')}
        subtitle={translate('unexpected_error_notified')}
        image={<Alert />}
        hideAdditionalInfo
        testID="attendee-messaging-flyout-loading-channel-error"
      />
    );
  }
  const newMessageButton = isAttendeeListEnabled ? (
    <div css={styles.newMessage}>
      <Link href={`/events/${eventId}/messages?newChannel=true`} passHref legacyBehavior>
        <Button
          appearance="filled"
          accessibilityLabel={
            flyoutStyle
              ? translate('AttendeeMessaging-Empty-Button-Send-Message')
              : translate('AttendeeMessaging-Empty-Button-New-Message')
          }
          text={
            flyoutStyle
              ? translate('AttendeeMessaging-Empty-Button-Send-Message')
              : translate('AttendeeMessaging-Empty-Button-New-Message')
          }
          element="a"
          testID="attendee-messaging-flyout-channel-list-view-message-link"
          onClick={() => onNavFlyoutClose()}
        />
      </Link>
    </div>
  ) : null;
  return (
    <div css={styles.container} {...injectTestId('attendee-messaging-flyout-channel-list-container')}>
      {isLoadingChannels && (
        <div css={styles.loadingOverlay}>
          <LoadingSpinner size="m" testID="attendee-messaging-flyout-loading-spinner" />
        </div>
      )}
      <Chat client={client} i18nInstance={i18nInstance}>
        <StreamChannelList
          onAddedToChannel={onAddedToChannel}
          setActiveChannelOnMount={false}
          sendChannelsToList
          Paginator={props => (
            // pass false to hasNextPage to hide Load More button in the flyout
            <LoadMorePaginator {...props} hasNextPage={false} />
          )}
          List={props => (
            <MessagingChannelList
              {...props}
              setIsLoadingChannels={setIsLoadingChannels}
              setLoadedChannels={updateLoadedChannels}
              setLoadingChannelsError={setLoadingChannelsError}
            />
          )}
          Preview={(props: ChannelPreviewUIComponentProps<StreamType>) => (
            <MessagingChannelPreview {...props} onClick={onNavFlyoutClose} variant="compact" />
          )}
          {...filterCriteria}
          EmptyStateIndicator={props => <EmptyChannelList {...props} variant="compact" />}
        />
      </Chat>
      <div css={styles.actions} {...injectTestId('attendee-messaging-flyout-channel-list-actions')}>
        {hasLoadedChannels ? (
          <>
            {flyoutStyle ? (
              <Link href={`/events/${eventId}/messages`} passHref legacyBehavior>
                <Button
                  appearance="lined"
                  testID="attendee-messaging-flyout-channel-list-view-message-link"
                  onClick={onNavFlyoutClose}
                  text={translate('AttendeeMessaging-Button-View-All-Messages')}
                />
              </Link>
            ) : (
              <Link href={`/events/${eventId}/messages`} passHref legacyBehavior>
                <TextLink
                  size="l"
                  testID="attendee-messaging-flyout-channel-list-view-message-link"
                  onClick={onNavFlyoutClose}
                >
                  {translate('AttendeeMessaging-Button-View-All-Messages')}
                </TextLink>
              </Link>
            )}
          </>
        ) : (
          newMessageButton
        )}
      </div>
    </div>
  );
}

export default ChannelList;
